import React, { useCallback, useMemo, useRef } from "react";
import { addStyles, EditableMathField } from "react-mathquill";

// Ensure MathQuill base styles are added once
addStyles();

/**
 * Keypad layout (5 rows x 6 cols)
 * 1) Digits & basic ops
 * 2) Close paren & mult/div
 * 3) Exponents & fraction
 * 4) Constants/roots & backspace
 * 5) Trig & arrows & equals
 */
const GRID_ITEMS = [
  "7", "8", "9", "+", "-", "(",
  "4", "5", "6", ")", "×", "÷",
  "1", "2", "3", "x²", "xⁿ", "a/b",
  "0", ".", "π", "√", "ⁿ√", "=",
  "sin", "cos", "tan", "←", "→", "⌫",
];

export default function MathAnswerField({ value, onChange, onEnter, disabled = false }) {
  const mqRef = useRef(null);

  const handleMount = useCallback((mq) => {
    mqRef.current = mq;
  }, []);

  const handleKey = useCallback((key) => {
    const mq = mqRef.current;
    if (!mq) return;

    const write   = (s) => mq.write(s);
    const cmd     = (c) => mq.cmd(c);
    const stroke  = (s) => mq.keystroke(s);
    const typed   = (t) => (mq.typedText ? mq.typedText(t) : stroke(t));

    switch (key) {
      // math ops & symbols
      case "×": cmd("\\times"); break; // Use \times instead of \cdot for better visibility
      case "÷": cmd("\\div"); break;
      case "π": cmd("\\pi"); break;
      case "√": cmd("\\sqrt"); break;

      // nth root: insert \sqrt[n]{} and move caret to the n for immediate edit
      case "ⁿ√":
        write("\\sqrt[n]{}");
        // move into the index [n]
        stroke("Left"); // into braces {}
        stroke("Left"); // into [n]
        break;

      // exponents / fraction helpers
      case "x²":
        write("x");
        typed("^");
        write("2");
        break;
      case "xⁿ":
        write("x");
        typed("^"); // leaves caret in exponent
        break;
      case "a/b":
        cmd("\\frac"); // caret starts in numerator
        break;

      // trig functions with parentheses, caret inside
      case "sin":
        cmd("\\sin"); write("()"); stroke("Left"); break;
      case "cos":
        cmd("\\cos"); write("()"); stroke("Left"); break;
      case "tan":
        cmd("\\tan"); write("()"); stroke("Left"); break;

      // navigation / editing
      case "⌫": stroke("Backspace"); break;
      case "←": stroke("Left"); break;
      case "→": stroke("Right"); break;

      // plain characters
      case "+":
      case "-":
      case "(":
      case ")":
      case "=":
      case ".":
      case "0":
      case "1":
      case "2":
      case "3":
      case "4":
      case "5":
      case "6":
      case "7":
      case "8":
      case "9":
        write(key);
        break;

      default:
        break;
    }

    mq.focus();
  }, []);

  const keypad = useMemo(() => {
    return (
      <div className="mt-2 sm:mt-3">
        <div className="grid grid-cols-6 gap-1.5 sm:gap-2">
          {GRID_ITEMS.map((label) => (
            <button
              key={label}
              type="button"
              aria-label={`insert ${label}`}
              title={`Insert ${label}`}
              onClick={() => handleKey(label)}
              className="min-h-[44px] rounded-xl shadow-sm text-base sm:text-lg bg-gray-100 hover:bg-gray-200 active:bg-gray-300 text-gray-900"
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    );
  }, [handleKey]);

  return (
    <div className="w-full">
      {/* Tidy up MathQuill field visuals */}
      <style>{`
        .mq-editable-field, .mq-editable-field .mq-root-block { border: none !important; box-shadow: none !important; }
        .mq-editable-field { padding: 8px 12px; min-height: 56px; cursor: text; line-height: 1.6; }
        .mq-editable-field .mq-root-block { overflow: visible; }
        .mq-editable-field.mq-empty .mq-root-block:before { content: none !important; }
        .mq-root-block { border: none !important; }
        /* Fix square root gap at the top */
        .mq-sqrt-prefix { 
          margin-top: 0 !important; 
          padding-top: 0 !important; 
          top: 0 !important;
        }
        .mq-sqrt-stem { 
          margin-top: 0 !important; 
          padding-top: 0 !important; 
          top: 0 !important;
        }
        .mq-sqrt { 
          vertical-align: baseline !important;
          margin-top: 0 !important;
          padding-top: 0 !important;
        }
        .mq-sqrt > .mq-sqrt-prefix {
          margin-top: 0 !important;
          padding-top: 0 !important;
        }
        /* Ensure multiplication symbol displays correctly */
        .mq-non-leaf, .mq-scaled { vertical-align: baseline !important; }
        .mq-binary-operator { vertical-align: baseline !important; }
      `}</style>

      <div
        className={`w-full rounded-2xl border bg-white shadow p-2 sm:p-3 md:p-4 focus-within:ring-2 focus-within:ring-teal-500 overflow-visible ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-text'}`}
        onClick={() => { if (!disabled) { try { mqRef.current?.focus(); } catch {} } }}
      >
        <EditableMathField
          latex={value || ""}
          onChange={(mf) => { if (!disabled) onChange?.(mf.latex()); }}
          mathquillDidMount={handleMount}
          onKeyDown={(e) => {
            if (disabled) {
              e.preventDefault();
              return;
            }
            if (e.key === "Enter") {
              e.preventDefault();
              onEnter?.();
            }
          }}
          style={{ minHeight: 56, fontSize: 22, outline: "none", lineHeight: 1.6 }}
          config={{ readOnly: disabled }}
        />
      </div>

      {!disabled && keypad}
    </div>
  );
}